<template>
  <div class="phone">
    <header>
      <div class="title">
        <span class="back" @click="goBack">◀</span>
        <span>{{ $t('任务下单中心') }}</span>
      </div>
      <button @click="toLink(`/user/taskOrderDetail`)" class="record" type="button">
        <span>📝</span><span>{{ $t('记录') }}</span>
      </button>
    </header>

    <main>
      <!-- 倒计时卡片 -->
      <section class="count-card">
        <div class="card-title">
          <span class="icon">⏱</span><span>{{ $t('任务倒计时') }}</span>
        </div>
        <div class="timer-row">
          <!--          <span>{{ $t('倒计时后自动放弃') }}:</span>-->
          <strong class="timer">{{ taskTimeFormatted }}</strong>
        </div>
        <div class="business">
          {{ $t('业务码') }}: <b>{{ periodNumber }}</b>
        </div>
      </section>

      <!-- 商品分类（一级分类，多选） -->
      <section class="section">
        <h2>{{ $t('选择商品分类') }}（{{ $t('可多选') }}）</h2>
        <div class="tabs" v-if="!loadingCategories && level1Categories.length">
          <button
              v-for="cat in level1Categories"
              :key="cat.id"
              class="tab"
              :class="{ active: selectedLevel1Ids.includes(cat.id) }"
              type="button"
              @click="toggleCategoryLevel1(cat.id)"
          >
            <span v-if="cat.pic" class="cat-pic"><img :src="getFileViewUrl(cat.pic)" :alt="$t(cat.name)"/></span>
            <span v-else>{{ cat.icon }}</span>
            <span>{{ $t(cat.name) }}</span>
          </button>
        </div>
        <div v-else-if="loadingCategories" class="loading-tabs">{{ $t('加载分类中...') }}</div>
        <div v-else class="no-cates">{{ $t('暂无分类') }}</div>
      </section>

      <!-- 产品类别（二级分类，每页4个轮播） -->

      <section v-if="subCategoryGroups.length" class="section">
        <div class="product-head">
          <h2>{{ $t('产品类别') }}</h2>
        </div>
        <div class="product-wrap">
          <van-swipe
              class="category-swipe"
              :autoplay="3000"
              indicator-color="#fe2c55"
              :loop="true"
              :show-indicators="true"
          >
            <van-swipe-item v-for="(group, idx) in subCategoryGroups" :key="idx">
              <div class="products-grid">
                <div
                    v-for="sub in group"
                    :key="sub.id"
                    class="product-card"
                >
                  <div v-if="sub.pic" class="thumb-img">
                    <img :src="getFileViewUrl(sub.pic)"/>
                  </div>
                  <div v-else class="thumb-placeholder">{{ sub.icon || '📦' }}</div>
                  <span>{{ $t(sub.name) }}</span>
                </div>
              </div>
            </van-swipe-item>
          </van-swipe>
        </div>
      </section>

      <!-- 账户余额 -->
      <section class="info-card">
        <div class="balance-row">
          <span>{{ $t('账户余额') }}:</span>
          <strong>{{ formatMoney(userMoney ? userMoney.balance : 0) }}</strong>
        </div>
      </section>

      <!-- 下单金额 -->
      <section class="order-card">
        <div class="label">{{ $t('订单金额') }}</div>
        <input
            class="quantity"
            v-model.number="orderAmount"
            type="number"
            inputmode="numeric"
            min="1"
        />
      </section>

      <button class="primary" :disabled="taskSeconds <= 0" @click="openDetail">
        {{ $t(taskSeconds <= 0 ? '任务已自动放弃' : '立即结算') }}
      </button>
    </main>

    <!-- 详情弹窗（直接结算） -->
    <div v-if="showDetailOverlay" class="custom-overlay" @click.self="closeDetail">
      <div class="custom-modal">
        <div class="modal-title"><span class="icon">🛒</span><span>{{ $t('商品分类') }}</span></div>
        <div class="modal-line">
          <span>{{ selectedCategoriesText }}</span>
        </div>
        <div class="modal-title" style="margin-top:15px;"><span class="icon">📋</span><span>{{ $t('订单详情') }}</span>
        </div>
        <div class="modal-line">
          <b>{{ $t('总订单金额') }}</b>
          <b class="blue">{{ totalAmount }}</b>
        </div>
        <div class="modal-title" style="margin-top:15px;"><span class="icon">💰</span><span>{{ $t('预计收益') }}</span>
        </div>
        <div class="modal-line">
          <span>{{ $t('预计佣金收益') }}</span>
          <b class="orange">+{{ expectedIncome }}</b>
        </div>
        <div class="modal-line">
          <span>{{ $t('收益率') }}</span>
          <b class="orange">{{ commissionRate }}%</b>
        </div>
        <button class="confirm-btn" type="button" @click="openSettle">{{ $t('确认') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import {getListWithChildren} from "@/api/goods"
import {createTaskOrder, getCurrPeriod} from "@/api/task"

const defaultIcons = {
  '服装': '👕', '数码': '📱', '家居': '🏠', '美食': '🍜', '美妆': '💄',
  '服饰': '👗', '家电': '⚡', '图书': '📚', '运动': '⚽'
}

export default {
  name: "TaskOrderCenter",
  data() {
    return {
      level1Categories: [],
      selectedLevel1Ids: [],
      allSubCategories: [],
      loadingCategories: true,
      taskSeconds: 0,
      orderAmount: 100,
      showDetailOverlay: false,
      countdownTimer: null,
      periodNumber: '',
      periodMillisecond: 0
    }
  },
  computed: {
    taskTimeFormatted() {
      const minutes = Math.floor(this.taskSeconds / 60)
      const seconds = this.taskSeconds % 60
      return `00:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    filteredSubCategories() {
      if (!this.selectedLevel1Ids.length) return []
      return this.allSubCategories.filter(sub => this.selectedLevel1Ids.indexOf(sub.parentId) !== -1)
    },
    subCategoryGroups() {
      const list = this.filteredSubCategories
      const groups = []
      for (let i = 0; i < list.length; i += 4) {
        groups.push(list.slice(i, i + 4))
      }
      return groups
    },
    selectedCategoriesText() {
      const names = this.level1Categories
          .filter(cat => this.selectedLevel1Ids.indexOf(cat.id) !== -1)
          .map(cat => this.$t(cat.name))
      return names.join('、') || ''
    },
    totalAmount() {
      const amount = Math.max(1, this.orderAmount || 1)
      return amount * this.selectedLevel1Ids.length
    },
    // 收益率：优先使用 userInfo.taskRate（>0），否则从全局 paramMapAll 读取
    commissionRate() {
      let rate = 0
      if (this.userInfo && this.userInfo.taskRate > 0) {
        rate = Number(this.userInfo.taskRate)
      } else {
        // 读取全局参数
        if (typeof window !== 'undefined' && window.paramMapAll && window.paramMapAll['TASK_COMMISSION_RATE'] !== undefined) {
          rate = Number(window.paramMapAll['TASK_COMMISSION_RATE'])
        } else if (this.$root && this.$root.paramMapAll && this.$root.paramMapAll['TASK_COMMISSION_RATE'] !== undefined) {
          rate = Number(this.$root.paramMapAll['TASK_COMMISSION_RATE'])
        }
      }
      return isNaN(rate) ? 0 : rate
    },
    expectedIncome() {
      const rate = this.commissionRate
      const amount = Math.max(1, this.orderAmount || 1)
      const totalAmount = amount * this.selectedLevel1Ids.length
      return (totalAmount * rate / 100).toFixed(2)
    }
  },
  mounted() {
    this.fetchCurrPeriod()
    this.getCateList()
  },
  beforeDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  },
  methods: {
    // 从后端获取当前期数信息
    async fetchCurrPeriod() {
      try {
        const res = await getCurrPeriod()
        // res 经过 axios 拦截器处理后是 Response body JSON：{ code:0, date:"...", data:{...} }
        const inner = (res && res.data) ? res.data : res
        if (inner) {
          const ms = parseInt(inner.millisecond) || 0
          this.periodMillisecond = ms > 0 ? ms : 180 * 1000
          this.periodNumber = inner.periodNumber || ''
        }
      } catch (e) {
        console.error('获取期数信息失败:', e)
        this.periodMillisecond = 180 * 1000
      }
      this.taskSeconds = Math.ceil(this.periodMillisecond / 1000)
      this.startCountdown()
    },
    startCountdown() {
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        if (this.periodMillisecond > 0) {
          this.periodMillisecond -= 1000
          this.taskSeconds = Math.ceil(this.periodMillisecond / 1000)
        } else {
          // 当前期结束，延迟后重新获取下一期
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
          setTimeout(() => {
            this.fetchCurrPeriod()
          }, 500)
        }
      }, 1000)
    },
    resetTask() {
      // 下单成功后刷新当前期数
      this.fetchCurrPeriod()
    },
    goBack() {
      this.$router.go(-1)
    },
    async getCateList() {
      this.loadingCategories = true
      try {
        const res = await getListWithChildren()
        if (res && res.data && res.data.length) {
          this.processCategoryTree(res.data)
          if (this.level1Categories.length > 0) {
            this.selectedLevel1Ids = [this.level1Categories[0].id]
          }
        } else {
          this.useFallbackCategories()
        }
      } catch (error) {
        console.error('获取分类失败:', error)
        this.useFallbackCategories()
      } finally {
        this.loadingCategories = false
      }
    },
    processCategoryTree(nodes) {
      const level1 = []
      const allSub = []
      nodes.forEach(node => {
        if (!node.pid) {
          level1.push({
            id: node.id,
            name: node.name,
            pic: node.pic,
            icon: this.getIconByName(node.name)
          })
          if (node.children && node.children.length) {
            node.children.forEach(child => {
              allSub.push({
                id: child.id,
                name: child.name,
                pic: child.pic,
                icon: this.getIconByName(child.name),
                parentId: node.id
              })
            })
          }
        }
      })
      this.level1Categories = level1
      this.allSubCategories = allSub
    },
    getIconByName(name) {
      return defaultIcons[name] || '📦'
    },
    useFallbackCategories() {
      this.level1Categories = [
        {id: 1, name: '服装', icon: '👕', pic: null},
        {id: 2, name: '数码', icon: '📱', pic: null},
        {id: 3, name: '家居', icon: '🏠', pic: null}
      ]
      this.allSubCategories = [
        {id: 11, name: '男装', icon: '👔', pic: null, parentId: 1},
        {id: 12, name: '女装', icon: '👗', pic: null, parentId: 1},
        {id: 21, name: '手机', icon: '📱', pic: null, parentId: 2},
        {id: 22, name: '电脑', icon: '💻', pic: null, parentId: 2},
        {id: 31, name: '家具', icon: '🛋️', pic: null, parentId: 3},
        {id: 32, name: '厨具', icon: '🍳', pic: null, parentId: 3}
      ]
      this.selectedLevel1Ids = [1]
      this.loadingCategories = false
    },
    toggleCategoryLevel1(catId) {
      if (this.selectedLevel1Ids.indexOf(catId) !== -1) {
        if (this.selectedLevel1Ids.length === 1) return
        this.selectedLevel1Ids = this.selectedLevel1Ids.filter(id => id !== catId)
      } else {
        this.selectedLevel1Ids.push(catId)
      }
    },
    openDetail() {
      if (this.taskSeconds <= 0) return
      if (this.selectedLevel1Ids.length === 0) {
        return
      }
      this.showDetailOverlay = true
    },
    closeDetail() {
      this.showDetailOverlay = false
    },
    // 直接结算（取消第二个弹窗）
    async openSettle() {
      if (this.taskSeconds <= 0) {
        this.$toast(this.$t('任务已自动放弃'))
        return
      }
      if (this.selectedLevel1Ids.length === 0) {
        this.$toast(this.$t('请选择商品分类'))
        return
      }
      const requestData = {
        cateIds: this.selectedLevel1Ids,
        amt: this.orderAmount
      }
      await createTaskOrder(requestData)
      this.$toast.success(this.$t('下单成功'))
      this.showDetailOverlay = false
      this.resetTask()
    }
  }
}
</script>

<style scoped>
/* ========== 完整样式 ========== */
:root {
  --bg: #f6f8fc;
  --panel: #fff;
  --text: #15243a;
  --muted: #7a8799;
  --line: #e9edf4;
  --blue: #00c7d8;
  --orange: #fe2c55;
  --red: #fe2c55;
  --shadow: 0 12px 30px rgba(22, 24, 35, .08);
  --soft-shadow: 0 8px 20px rgba(22, 24, 35, .06);
}

* {
  box-sizing: border-box;
}

.phone {
  width: min(100vw, 383px);
  min-height: 100vh;
  background: #f7f8fa;
  overflow: hidden;
  position: relative;
  box-shadow: 0 0 0 1px rgba(18, 33, 59, 0.04), 0 24px 70px rgba(15, 31, 57, 0.12);
  margin: 0 auto;
}

header {
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  background: linear-gradient(135deg, #111318 0%, #171a22 58%, #222630 100%);
  border-bottom: 0;
  box-shadow: 0 10px 24px rgba(10, 12, 18, 0.18);
  position: sticky;
  top: 0;
  z-index: 5;
}

.title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 18px;
  color: #fff;
  letter-spacing: 0.2px;
}

.back {
  width: 17px;
  height: 17px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: linear-gradient(135deg, #25f4ee 0 47%, #fe2c55 48% 100%);
  color: #111318;
  font-weight: 900;
  box-shadow: 0 4px 12px rgba(37, 244, 238, 0.18);
  font-size: 11px;
  cursor: pointer;
}

.record {
  border: 0;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 999px;
}

main {
  padding: 14px 14px 50px;
}

.count-card {
  position: relative;
  overflow: hidden;
  color: #fff;
  padding: 20px 18px;
  background: linear-gradient(135deg, rgba(37, 244, 238, 0.18), transparent 34%),
  linear-gradient(155deg, #171a21 0%, #111318 68%, #090a0d 100%);
  border: 0;
  border-radius: 14px;
  box-shadow: 0 18px 34px rgba(13, 15, 22, 0.22);
}

.count-card::after {
  content: "";
  position: absolute;
  right: -30px;
  top: -36px;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(254, 44, 85, 0.42), transparent 66%);
  pointer-events: none;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 900;
  font-size: 16px;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.card-title .icon {
  width: 21px;
  height: 21px;
  display: grid;
  place-items: center;
  font-size: 17px;
  border-radius: 7px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(22, 24, 35, 0.08);
}

.timer-row {
  display: flex;
  align-items: baseline;
  gap: 9px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  position: relative;
  z-index: 1;
}

.timer {
  color: #ff3b61;
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 2px;
  text-shadow: 0 0 18px rgba(254, 44, 85, 0.35);
  font-variant-numeric: tabular-nums;
}

.business {
  padding-top: 12px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 13px;
  position: relative;
  z-index: 1;
}

.business b {
  color: #fff;
  font-weight: 800;
  margin-left: 6px;
}

.section {
  margin-top: 20px;
}

.section h2 {
  margin: 0 0 12px;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 800;
  color: #171a22;
}

.section h2::before {
  content: "";
  width: 5px;
  height: 15px;
  border-radius: 4px;
  background: linear-gradient(180deg, #25f4ee, #fe2c55);
  flex: 0 0 auto;
}

.tabs {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 0 0 4px;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  min-width: 78px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: #fff;
  color: #272a34;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(22, 24, 35, 0.06);
  flex: 0 0 auto;
  position: relative;
  transition: transform 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.tab.active {
  color: #fff;
  border-color: #111318;
  background: linear-gradient(135deg, #111318, #20232d);
  box-shadow: 0 10px 22px rgba(22, 24, 35, 0.22), 4px 4px 0 rgba(254, 44, 85, 0.22), -4px -4px 0 rgba(37, 244, 238, 0.18);
}

.tab.active::after {
  content: "✓";
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fe2c55;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  display: grid;
  place-items: center;
  position: absolute;
  right: -3px;
  top: -3px;
}

.product-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.product-wrap {
  position: relative;
  min-height: 98px;
}

.category-swipe {
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 20px rgba(22, 24, 35, 0.06);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 11px;
  padding: 12px;
  background: #fff;
}

.product-card {
  min-height: 98px;
  border: 1px solid rgba(22, 24, 35, 0.06);
  border-radius: 12px;
  background: #fff;
  padding: 10px 8px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 22px rgba(22, 24, 35, 0.06);
  cursor: default;
}

.thumb-img,
.thumb-placeholder {
  width: 46px;
  height: 46px;
  border-radius: 9px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  box-shadow: inset 0 0 0 1px rgba(22, 24, 35, 0.05), 0 6px 14px rgba(22, 24, 35, 0.08);
  overflow: hidden;
}

.thumb-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-card span:last-child {
  font-size: 12px;
  font-weight: 800;
  color: #171a22;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.info-card {
  margin-top: 22px;
  border: 1px solid rgba(22, 24, 35, 0.05);
  background: #fff;
  border-radius: 13px;
  padding: 16px;
  box-shadow: 0 10px 24px rgba(22, 24, 35, 0.06);
}

.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 800;
}

.balance-row strong {
  color: #fe2c55;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.order-card {
  margin-top: 18px;
  padding: 18px 16px 22px;
  background: linear-gradient(180deg, #fff, #f8fbff);
  border: 1px solid #e8eef7;
  border-radius: 13px;
  box-shadow: 0 10px 24px rgba(22, 24, 35, 0.06);
}

.label {
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 11px;
}

.quantity {
  width: 100%;
  height: 42px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  color: #1f2d43;
  font-size: 16px;
  font-weight: 800;
  text-align: center;
  outline: none;
  box-shadow: inset 0 1px 4px rgba(31, 48, 78, 0.04);
}

.quantity:focus {
  border-color: #25f4ee;
  box-shadow: 0 0 0 3px rgba(37, 244, 238, 0.16);
}

.primary {
  width: 100%;
  height: 47px;
  border: 0;
  border-radius: 10px;
  margin-top: 20px;
  background: linear-gradient(135deg, #fe2c55, #ff4d2e);
  color: #fff;
  font-size: 15px;
  font-weight: 900;
  box-shadow: 0 12px 22px rgba(254, 44, 85, 0.24);
  cursor: pointer;
}

.primary:disabled {
  background: #59606d;
  color: rgba(255, 255, 255, 0.72);
  box-shadow: none;
  cursor: not-allowed;
}

.custom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 12, 18, 0.58);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 1000;
}

.custom-modal {
  width: min(345px, calc(100vw - 36px));
  background: #fff;
  border-radius: 16px;
  padding: 20px 18px 18px;
  box-shadow: 0 24px 70px rgba(10, 12, 18, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.8);
  animation: pop 0.16s ease-out;
}

@keyframes pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 900;
  font-size: 16px;
  margin-bottom: 7px;
}

.modal-title.center {
  justify-content: center;
  margin-bottom: 17px;
}

.modal-title .icon {
  width: 21px;
  height: 21px;
  display: grid;
  place-items: center;
  font-size: 17px;
  border-radius: 7px;
  background: #f3f7ff;
  box-shadow: inset 0 0 0 1px #dbe7fb;
}

.modal-line {
  min-height: 43px;
  border-top: 1px solid #edf0f3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #6d7280;
}

.modal-line:last-of-type {
  border-bottom: 1px solid #eef1f6;
}

.modal-line b {
  color: #171a22;
  font-weight: 900;
}

.modal-line .blue {
  color: #00b3c2;
}

.modal-line .orange {
  color: #fe2c55;
}

.confirm-btn {
  width: 100%;
  height: 43px;
  margin-top: 16px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #fe2c55, #ff4d2e);
  color: #fff;
  font-weight: 900;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(254, 44, 85, 0.2);
}

.count-small {
  color: #607087;
  font-size: 13px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.count-small b {
  color: #5e78a0;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.cat-pic {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
}

.cat-pic img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.loading-tabs, .no-cates {
  padding: 12px;
  text-align: center;
  color: #7a8799;
  font-size: 13px;
}

@media (max-width: 340px) {
  main {
    padding-inline: 10px;
  }

  header {
    padding-inline: 12px;
  }

  .tab {
    min-width: 68px;
  }
}
</style>